import * as React from 'react';
import { View, StyleSheet, Pressable } from 'react-native';
import { Button, Portal, Text, IconButton } from 'react-native-paper';

export interface IConfirmBox {
    title: string;
    message: string;
    yesCallback: any;
    noCallback?: any;
    yesText?: string;
    noText?: string;
}

const ConfirmBox: React.FC<IConfirmBox> = (props) => {
    
    const getDialogIcon = () => {
        const titleLower = props.title.toLowerCase();
        if (titleLower.includes('logout')) {
            return { name: 'logout', color: '#BA1A1A', bg: '#FFDAD6' };
        }
        if (titleLower.includes('switch')) {
            return { name: 'account-switch-outline', color: '#102B27', bg: '#CBEBE4' };
        }
        if (titleLower.includes('delete') || titleLower.includes('remove') || titleLower.includes('clear')) {
            return { name: 'trash-can-outline', color: '#BA1A1A', bg: '#FFDAD6' };
        }
        return { name: 'alert-circle-outline', color: '#0061A4', bg: '#D1E4FF' };
    };

    const iconInfo = getDialogIcon();

    return (
        <Portal>
            <View style={styles.overlayContainer}>
                {/* Clicking on the translucent background dismisses the popup */}
                <Pressable style={styles.dismissOverlay} onPress={props.noCallback} />
                
                {/* The main confirmation card */}
                <View style={styles.dialogCard}>
                    {/* Title with Icon */}
                    <View style={styles.titleContainer}>
                        <View style={[styles.iconWrapper, { backgroundColor: iconInfo.bg }]}>
                            <IconButton 
                                icon={iconInfo.name} 
                                iconColor={iconInfo.color} 
                                size={20} 
                                style={styles.icon}
                            />
                        </View>
                        <Text style={styles.title}>{props.title}</Text>
                    </View>

                    {/* Message Body */}
                    <View style={styles.content}>
                        <Text variant="bodyMedium" style={styles.message}>
                            {props.message}
                        </Text>
                    </View>

                    {/* Action Buttons */}
                    <View style={styles.actions}>
                        <Button 
                            mode="outlined"
                            style={styles.noButton}
                            labelStyle={styles.noButtonText}
                            onPress={props.noCallback}
                            textColor="#50605D"
                        >
                            {props.noText || 'Cancel'}
                        </Button>
                        <Button 
                            mode="contained"
                            style={styles.yesButton}
                            labelStyle={styles.yesButtonText}
                            onPress={props.yesCallback}
                            buttonColor={iconInfo.color === '#BA1A1A' ? '#BA1A1A' : '#102B27'}
                        >
                            {props.yesText || 'Confirm'}
                        </Button>
                    </View>
                </View>
            </View>
        </Portal>
    );
};

const styles = StyleSheet.create({
    overlayContainer: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 99999, // extremely high stack order to render above all sidebars, topbars, and portals
        ...StyleSheet.absoluteFillObject,
    },
    dismissOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(16, 43, 39, 0.4)', // beautiful darkened translucent backdrop overlay
    },
    dialogCard: {
        backgroundColor: '#FFFFFF',
        borderRadius: 16,
        padding: 4,
        width: '90%',
        maxWidth: 440,
        minWidth: 300,
        shadowColor: '#102B27',
        shadowOffset: {
            width: 0,
            height: 12,
        },
        shadowOpacity: 0.18,
        shadowRadius: 16.00,
        elevation: 24,
        borderWidth: 1,
        borderColor: 'rgba(16, 43, 39, 0.08)',
        alignSelf: 'center',
        zIndex: 100000,
    },
    titleContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingTop: 16,
        paddingHorizontal: 20,
    },
    iconWrapper: {
        width: 36,
        height: 36,
        borderRadius: 18,
        justifyContent: 'center',
        alignItems: 'center',
    },
    icon: {
        margin: 0,
    },
    title: {
        fontWeight: '700',
        color: '#102B27',
        fontSize: 18,
        letterSpacing: 0.25,
    },
    content: {
        paddingHorizontal: 20,
        paddingVertical: 12,
    },
    message: {
        color: '#50605D',
        lineHeight: 22,
        fontSize: 14.5,
    },
    actions: {
        paddingHorizontal: 20,
        paddingBottom: 16,
        paddingTop: 8,
        gap: 8,
        flexDirection: 'row',
        justifyContent: 'flex-end',
    },
    noButton: {
        borderRadius: 8,
        borderColor: 'rgba(16, 43, 39, 0.2)',
        borderWidth: 1,
        minWidth: 90,
    },
    noButtonText: {
        fontSize: 13,
        fontWeight: '600',
        letterSpacing: 0.3,
    },
    yesButton: {
        borderRadius: 8,
        minWidth: 90,
        shadowColor: '#000',
        shadowOffset: {
            width: 0,
            height: 2,
        },
        shadowOpacity: 0.1,
        shadowRadius: 2.5,
        elevation: 3,
    },
    yesButtonText: {
        fontSize: 13,
        fontWeight: '700',
        color: '#FFFFFF',
        letterSpacing: 0.3,
    }
});

export default ConfirmBox;
